import MoreHoriz from '@mui/icons-material/MoreHoriz'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Avatar,
  Box,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { FC, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { ClearRounded, DoneRounded, PhotoCamera, Lock } from '@mui/icons-material'
import { useMutation } from 'react-query'
import { AnimalsClient } from '../../../interactors/clients/AnimalsClient'
import { useAnimalStore } from '../../../store/AnimalStore'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { getAnimalProfilePhotoSrc } from '../../../utils/S3-links'
import { CircularProgressPanel } from '../../common/CircularProgressPanel'
import { AnimalAdoptionTab } from './Adoption/AnimalAdoptionTab'
import { AnimalGeneralCard } from './AnimalGeneralCard'
import { AllAnimalInsightsInfoBox } from './AnimalVaccinationImportantInfo'
import { DeleteAnimalPrompt } from './DeleteAnimalPrompt'
import { AnimalDocumentsAndPicturesTab } from './DocumentsAndPictures/AnimalDocumentsAndPicturesTab'
import { AnimalBreedCard } from './General/AnimalBreedCard'
import { AnimalResponsibilitiesCard } from './General/AnimalResponsabilityCard'
import { AnimalTakeOverCard } from './General/AnimalTakeOverCard'
import { AnimalHealthTab } from './Health/AnimalHealthTab'
import { HostedFamillyFollowUpsTab } from './HostedFammilyFollowUpsTab/HostedFamillyFollowUpsTab'

import {
  getColorForAnimalAdoptionStatus,
  getReadableAnimalAdoptionStatus,
  getReadableSex,
  getReadableSpecies,
  getReadableSterilizedForFerrets,
} from '../../../domain/Animal/AnimalDisplay'
import { useCanEditAnimal } from '../../../store/useCanEditAnimal'
import { useEditAnimalMutation } from '../../../store/useEditAnimalMutation'
import { colors } from '../../theme'
import InactivityPopup from '../../utils/InactivityPopup'
import { AnimalAboutCard } from './General/AnimalAboutCard'
import { AnimalNoteCard } from './General/AnimalNoteCard'
import { AnimalResumeAdoptionCard } from './General/AnimalResumeAdoptionCard'
import { AnimalOtherImagesCard } from './General/AnimalOtherImagesCard'
import { EditGeneralInfoAnimalPrompt } from './EditGeneralInfoAnimalPrompt'
import { useAccountsStore } from '../../../store/AccountsStore'
import { ShareAnimalForm } from './AnimalShareAnnounce'
import { DetailSurface } from '../../common/DetailSurface'
import { getSoftChipSx } from '../../common/chipStyles'
import { overviewTabSx, overviewTabsSx } from '../../common/overviewStyles'

type TabKey = 'general' | 'sante' | 'familles' | 'documents' | 'adoption'

export const AnimalDetailsScreen: FC = () => {
  const isWideDesktop = useMediaQuery('(min-width:1100px)')
  const globalSnackBarStore = useGlobalSnackbarStore()
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()
  const location = useLocation()
  const canEditAnimal = useCanEditAnimal()
  const editAnimalMutation = useEditAnimalMutation({})

  const [currentTabIndex, setCurrentTabIndex] = useState<TabKey>('general')

  const handleChange = (_: React.SyntheticEvent, newTabKey: TabKey) => {
    setCurrentTabIndex(newTabKey)
    navigate(`/animaux/${animalId}?tab=${newTabKey}`)
  }

  const { animalId } = useParams() as { animalId: string }
  const animalStore = useAnimalStore()

  const [isUploadDone, setIsUploadDone] = useState<boolean>(false)

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  useEffect(() => {
    const loadAnimal = async () => {
      await animalStore.changeSelectedAnimal(animalId)
    }

    loadAnimal()
  }, [animalId])

  const animal = animalStore.selectedAnimal

  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [previewURL, setPreviewURL] = useState<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null) // Create a ref

  const [editMode, setEditMode] = useState(false)
  const [tempName, setTempName] = useState(animal?.name || '')

  useEffect(() => {
    if (editMode && nameInputRef.current) {
      nameInputRef.current.focus() // Focus the input
      nameInputRef.current.select() // Select the text
    }
  }, [editMode])

  const handleImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files ? event.target.files[0] : null
    if (selectedFile) {
      setSelectedImage(selectedFile)
      setPreviewURL(URL.createObjectURL(selectedFile))
    }
    setIsUploadDone(false)
  }

  const uploadMutation = useMutation(
    async () => await AnimalsClient.uploadProfileImage(animal!.id, selectedImage as File),
    {
      onSuccess: (data) => {
        globalSnackBarStore.triggerSuccessMessage(`La photo de profil de ${animal!.name} a été mise à jour.`)
        setIsUploadDone(true)
        if (animal) {
          animal.images.profileImageKey = data.split('/').pop() as string
        }
      },
      onError: () => {
        globalSnackBarStore.triggerErrorMessage(
          `La photo de profil de ${animal!.name} a echoué. Il est probable que l'image soit trop grosse.`
        )
        setIsUploadDone(true)
      },
    }
  )

  const spacingItem = { marginTop: 2 }

  function a11yProps(tabKey: TabKey) {
    return {
      id: `simple-tab-${tabKey}`,
      value: tabKey,
      'aria-controls': `simple-tabpanel-${tabKey}`,
      key: tabKey,
    }
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const tabParam = queryParams.get('tab')
    const newTabKey = tabParam as TabKey
    setCurrentTabIndex(newTabKey || 'general')
  }, [location.search])

  if (animalStore.isLoading || !animal) {
    return <CircularProgressPanel />
  }

  const profileImageKey = animal.images?.profileImageKey

  // Handle the save action
  const handleSave = () => {
    animal.name = tempName
    editAnimalMutation.mutate(animal)
    setEditMode(false)
  }

  // Handle cancel action
  const handleCancel = () => {
    setTempName(animal?.name || '')
    setEditMode(false)
  }

  const isFerret = animal.species === 'ferret'
  const isFemale = animal.sex === 'female'
  const animalSterilization =
    animal.health?.sterilizedOrNeutered === 'true'
      ? isFemale
        ? 'stérilisée'
        : 'stérilisé'
      : animal.health?.sterilizedOrNeutered === 'false'
      ? isFemale
        ? 'non stérilisée'
        : 'non stérilisé'
      : 'stérilisation inconnue'
  const ferretSterilization = animal.health?.sterilizedForFerrets
  return (
    <Box sx={{ backgroundColor: 'transparent', minHeight: '100%' }}>
      <DetailSurface sx={{ mb: { xs: 2.5, md: 5 } }}>
        <InactivityPopup />
        <Stack
          direction={isWideDesktop ? 'row' : 'column'}
          alignItems={isWideDesktop ? 'center' : 'stretch'}
          justifyContent="space-between"
          spacing={2}
          sx={{
            width: '100%',
            flexWrap: 'nowrap',
          }}
        >
          <Stack
            direction="row"
            spacing={1.75}
            alignItems="center"
            sx={{
              minWidth: 0,
              width: '100%',
              flex: isWideDesktop ? '1 1 520px' : '0 0 auto',
              alignSelf: isWideDesktop ? 'auto' : 'flex-start',
            }}
          >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                alt={animal.name}
                src={previewURL || (profileImageKey && getAnimalProfilePhotoSrc(profileImageKey))}
                sx={{
                  width: { xs: 72, sm: 84, md: 104 },
                  height: { xs: 72, sm: 84, md: 104 },
                  border: '1px solid #E4E8F2',
                  boxShadow: '0 1px 2px rgba(32, 56, 100, 0.04)',
                }}
              />

              {canEditAnimal && (
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    right: -6,
                    bottom: -6,
                    width: 30,
                    height: 30,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(122, 129, 151, 0.18)',
                    '&:hover': { backgroundColor: 'white' },
                  }}
                >
                  <PhotoCamera sx={{ fontSize: 18, color: '#7A8197' }} />
                  <input hidden accept="image/*" multiple type="file" onChange={handleImageSelection} />
                </IconButton>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {editMode ? (
                <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                  <TextField
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    variant="standard"
                    inputRef={nameInputRef}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    autoFocus
                    sx={{
                      mr: 1,
                      input: { fontSize: { xs: '1.2rem', md: '1.5rem' }, fontWeight: 900, color: colors.cello },
                      width: '100%',
                    }}
                  />

                  <IconButton onClick={handleCancel} color="error" size="small">
                    <ClearRounded />
                  </IconButton>
                  <IconButton onClick={handleSave} color="primary" size="small">
                    <DoneRounded />
                  </IconButton>
                </Box>
              ) : (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.cello,
                    lineHeight: 1.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {animal?.adoption.newName ? `${animal?.adoption.newName} (ex ${animal?.name})` : animal?.name}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  fontWeight: 600,
                  color: '#7A8197',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {`${animal?.species ? getReadableSpecies(animal.species) : ''} ${
                  animal?.sex ? getReadableSex(animal.sex).toLowerCase() : ''
                }, ${
                  isFerret
                    ? ferretSterilization
                      ? getReadableSterilizedForFerrets(ferretSterilization)
                      : animalSterilization
                    : animalSterilization
                }`}
              </Typography>
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                <Chip
                  size="small"
                  label={getReadableAnimalAdoptionStatus(animal.adoption.status)}
                  sx={getSoftChipSx(getColorForAnimalAdoptionStatus(animal.adoption.status), {
                    emphasis: true,
                    height: 28,
                    fontSize: 12.5,
                    labelWeight: 600,
                  })}
                />
              </Box>
              {previewURL && !isUploadDone && (
                <LoadingButton
                  component="label"
                  variant="contained"
                  onClick={async () => {
                    await uploadMutation.mutate()
                  }}
                  loading={uploadMutation.isLoading}
                  sx={{
                    mt: 1.25,
                    textTransform: 'none',
                    borderRadius: 3.5,
                    fontWeight: 800,
                    py: 0.75,
                    minHeight: 40,
                    boxShadow: '0 8px 18px rgba(32, 56, 100, 0.18)',
                    backgroundColor: '#FCB402',
                    '&:hover': { backgroundColor: '#E9A900' },
                  }}
                >
                  Mettre a jour la photo
                </LoadingButton>
              )}
            </Box>

            {canEditAnimal && (
              <Box display="flex" alignItems="center" sx={{ ml: 0.5 }}>
                <IconButton onClick={handleOpenUserMenu} sx={{ color: '#7A8197' }}>
                  <MoreHoriz />
                </IconButton>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <DeleteAnimalPrompt animal={animal} />
                  <MenuItem
                    key="edit name"
                    onClick={() => {
                      setTempName(animal.name)
                      setEditMode(true)
                      handleCloseUserMenu()
                    }}
                  >
                    {'Modifier le nom'}
                  </MenuItem>
                  <EditGeneralInfoAnimalPrompt animal={animal} menuItem />
                </Menu>
              </Box>
            )}
          </Stack>

          <Box
            sx={{
              display: 'flex',
              justifyContent: isWideDesktop ? 'flex-end' : 'flex-start',
              width: '100%',
              flex: isWideDesktop ? '0 1 auto' : '0 0 auto',
              maxWidth: isWideDesktop ? 520 : '100%',
              minWidth: 0,
            }}
          >
            <Box sx={{ display: isWideDesktop ? 'none' : 'block', width: '100%' }}>
              <ShareAnimalForm animal={animal} />
            </Box>
          </Box>
        </Stack>

        <Box
          sx={{
            pt: '20px',
            display: isWideDesktop ? 'flex' : 'block',
            gap: isWideDesktop ? '10px' : 2,
            alignItems: 'stretch',
            width: '100%',
            overflow: 'visible',
          }}
        >
          <Box sx={{ minWidth: 0, display: 'flex', width: '100%', flex: '1 1 auto' }}>
            <AnimalGeneralCard animal={animal} />
          </Box>
          <Box
            sx={{
              display: isWideDesktop ? 'flex' : 'none',
              minWidth: 0,
              width: 'min(360px, 100%)',
              flex: '0 0 360px',
            }}
          >
            <ShareAnimalForm animal={animal} />
          </Box>
        </Box>

        <AllAnimalInsightsInfoBox animal={animal} />

        <Box sx={{ marginTop: 2 }}>
          <Tabs
            value={currentTabIndex}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="basic tabs example"
            sx={overviewTabsSx}
          >
            <Tab label="Général" sx={overviewTabSx} {...a11yProps('general')} />
            <Tab label="Santé" sx={overviewTabSx} {...a11yProps('sante')} />
            <Tab label="Adoption" sx={overviewTabSx} {...a11yProps('adoption')} />
            {accountsStore.isPremium() ? (
              <Tab
                label="FA & Déplacements"
                {...a11yProps('familles')}
                disabled={!accountsStore.isPremium()}
                icon={!accountsStore.isPremium() ? <Lock /> : undefined}
                iconPosition="start"
                sx={overviewTabSx}
              />
            ) : (
              <Tooltip
                title={
                  !accountsStore.isPremium()
                    ? "Souscrivez à l'offre Utile pour bénéficier de cette fonctionnalité."
                    : ''
                }
                arrow
              >
                <span>
                  <Tab
                    label="FA & Déplacements"
                    {...a11yProps('familles')}
                    disabled={!accountsStore.isPremium()}
                    icon={!accountsStore.isPremium() ? <Lock /> : undefined}
                    iconPosition="start"
                    sx={overviewTabSx}
                  />
                </span>
              </Tooltip>
            )}
            <Tab label="Documents" sx={overviewTabSx} {...a11yProps('documents')} />
          </Tabs>
        </Box>

        <TabPanel value={currentTabIndex} index={'general' as TabKey}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <AnimalResponsibilitiesCard animal={animal} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AnimalAboutCard animal={animal} />
              <AnimalOtherImagesCard animal={animal} standalone sx={spacingItem} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <AnimalResumeAdoptionCard animal={animal} />
              <AnimalTakeOverCard animal={animal} sx={spacingItem} />
              <AnimalBreedCard animal={animal} sx={spacingItem} />
              <AnimalNoteCard animal={animal} sx={spacingItem} />
              {/* <AnimalAboutCards animal={animal} /> */}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTabIndex} index={'sante' as TabKey}>
          <AnimalHealthTab animal={animal} />
        </TabPanel>

        <TabPanel value={currentTabIndex} index={'adoption' as TabKey}>
          <AnimalAdoptionTab animal={animal} />
        </TabPanel>

        <TabPanel value={currentTabIndex} index={'familles' as TabKey}>
          {accountsStore.isPremium() && <HostedFamillyFollowUpsTab animal={animal} />}
        </TabPanel>

        <TabPanel value={currentTabIndex} index={'documents' as TabKey}>
          <AnimalDocumentsAndPicturesTab animal={animal} />
        </TabPanel>
      </DetailSurface>
    </Box>
  )
}

interface TabPanelProps<T> {
  children?: React.ReactNode
  index: T
  value: T
}

function TabPanel<T>(props: TabPanelProps<T>) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}
